import { useCallback, useRef, useState } from 'react'
import { useFocusEffect } from '@react-navigation/native'
import { BillingProduct, EstimatedPrice } from '@src/types'
import { useDispatchAsync } from '@src/utils'
import { EstimateOneTimePaymentQuery } from '@src/graphql/estimateOneTimePayment.generated'
import { AllProductsQuery } from '@src/graphql/fetchProducts.generated'

interface EstimatedProductPrice {
  product: AllProductsQuery['allProducts']['products'][number]
  estimatedPrice: EstimatedPrice
}

interface PurchasableItem {
  product: AllProductsQuery['allProducts']['products'][number]
  quantity: number
}

interface EstimatedProductPricesHookParams {
  purchasableItems: PurchasableItem[]
}

export const useEstimatedProductPrices = ({
  purchasableItems,
}: EstimatedProductPricesHookParams) => {
  const [loading, setLoading] = useState(true)
  const [estimatedProductPrices, setEstimatedProductPrices] = useState<EstimatedProductPrice[]>([])
  const [error, setError] = useState<string>()
  // Tracks whether we've ever completed a successful fetch. Used to keep
  // already-loaded estimates on screen when the user navigates back and
  // `useFocusEffect` re-runs `reload` - without this we'd flash the
  // skeleton on every refocus.
  const hasFetchedSuccessfullyRef = useRef(false)

  const dispatchAsync = useDispatchAsync()

  const estimateOneTimePayment = useCallback(
    async ({ product, quantity }: PurchasableItem) => {
      const { estimateOneTimePayment } = await dispatchAsync<EstimateOneTimePaymentQuery>({
        type: 'marketplace/estimateOneTimePayment',
        payload: {
          purchasableItems: [{ product: product.key as BillingProduct, quantity }],
        },
      })
      return estimateOneTimePayment
    },
    [dispatchAsync],
  )

  const reload = useCallback(async () => {
    // Only show the skeleton on the first fetch (or after an error). On
    // subsequent focus-driven re-fetches we keep the previous estimates
    // visible while the new ones are being computed in the background.
    if (!hasFetchedSuccessfullyRef.current) {
      setLoading(true)
    }
    try {
      const estimates = await Promise.all(
        purchasableItems.map(async (purchasableItem) => ({
          product: purchasableItem.product,
          estimatedPrice: await estimateOneTimePayment(purchasableItem),
        })),
      )

      setEstimatedProductPrices(estimates)
      hasFetchedSuccessfullyRef.current = true
      setLoading(false)
      setError(undefined)
    } catch (error: any) {
      hasFetchedSuccessfullyRef.current = false
      setError(error.message)
      setLoading(false)
    }
  }, [purchasableItems, estimateOneTimePayment, setEstimatedProductPrices, setLoading, setError])

  useFocusEffect(
    useCallback(() => {
      reload()
    }, [reload]),
  )

  return { estimatedProductPrices, loading, error, reload }
}
